import React, { useCallback, useMemo, useState } from "react";
import ReactSelect from "react-select";
import classNames from "classnames";
import "components/Inputs/MultiSelect/MultiSelect.scss";
import {clone} from "lodash";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const isIncludingString = (string, option) => {
    let result = false;
    if (
        !string ||
        option.label.toLowerCase().includes(string.toLowerCase()) ||
        option.value.toLowerCase().includes(string.toLowerCase())
    ) {
        result = (option.value !== "all");
    }
    return result;
}

const DropdownIndicator = ({ isFocused }) =>  {
    if (isFocused) {
        return (
            <div className="select__dropdown-arrow">
                <ExpandLessIcon className="select__dropdown-icon"/>
            </div>
        );
    }

    return (
        <div className="select__dropdown-arrow">
            <ExpandMoreIcon className="select__dropdown-icon"/>
        </div>
    );
}

export const MultiSelect = ({
    className,
    classNameSelect,
    label,
    value,
    options = [],
    error,
    helperText,
    hideHelpText = false,
    isClearable = false,
    isSearchable = false,
    isMenuPortal = false,
    isLoading,
    isDisabled,
    menuPlacement = "auto",
    onChange,
    ...restProps
}) => {
    if (options && options.length > 0) {
        if (value.length !== options.length) {
            options = [{ value: 'all', label: 'Select All (Filtered)' }, ...options];
        }
    }
    const [ inputValue, setInputValue ] = useState("");

    const currentValue = useMemo(() => {
        if (Array.isArray(value)) {
            return value.map((item) => options.find((option) => option.value === item));
        }

        if (Array.isArray(options) ?? value) {
            return options.find((option) => option.value === String(value) || option.value === value);
        }

        return value;
    }, [ value, options ]);

    const handleChange = (opt, { option }) => {
        let newOpts = opt;
        let string = inputValue;

        if (option && option.value === "all") {
            let filteredOptions = clone(options);

            filteredOptions = filteredOptions.filter(
                filteredOption => (isIncludingString(string, filteredOption) && !newOpts.includes(filteredOption))
            );

            string = null;
            newOpts = newOpts
                .concat(filteredOptions)
                .filter(newOpt => newOpt.value !== "all");
        }
        if (newOpts && newOpts.length > 1) {
            value = newOpts;
        }
        if (newOpts) {
            const preparedOptionValue = newOpts.map((item) => item.value);
            onChange(preparedOptionValue);
        } else {
            onChange([]);
        }
    }

    const handleInputChange = useCallback((value, { action }) => {
        if (action !== "set-value"){
            setInputValue(value);
        }
    }, []);

    const filterOption = useCallback((value, query) => {
        if (value.value === "all") {
            return true;
        } else if (query) {
            return value.label.toLowerCase().includes(query.toLowerCase()) || value.value.toLowerCase().includes(query.toLowerCase());
        }

        return true
    }, [ ]);

    let rootProps = {
        ...restProps,
        menuPlacement: menuPlacement,
        className: classNames("select__body", classNameSelect),
        classNamePrefix: "select",
        options: options,
        isMulti: true,
        closeMenuOnSelect: false,
        blurInputOnSelect: false,
        isSearchable: isSearchable,
        isLoading: isLoading,
        isClearable: isClearable,
        inputValue: inputValue,
        filterOption: filterOption,
        onInputChange: handleInputChange,
        isDisabled: isDisabled || isLoading,
        value: currentValue || "",
        components: {
            DropdownIndicator,
        },
        onBlur: () => {},
        onChange: handleChange
    };

    if (isMenuPortal) {
        rootProps = {
            ...rootProps,
            menuPortalTarget: document.body
        };
    }

    return (
        <div
            className={
                classNames(
                    "select",
                    { "select--helper-message": helperText },
                    { "select--error": error },
                    className
                )
            }
        >
            { label && (<div className="select__label">{ label }</div>) }

            <ReactSelect {...rootProps} />

            { !hideHelpText && <div className="select__helper-message">{ error || helperText }</div> }
        </div>
    );
};
